import { Children, cloneElement } from 'react';
import { get, isFunction, isString } from 'lodash';

// check if two components are the same type
const isTypeEqual = (component1: any, component2: any) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const type1 = component1.type;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const type2 = component2.type;

  if (isString(type1) || isString(type2)) {
    return type1 === type2;
  }

  if (isFunction(type1) && isFunction(type2)) {
    return (type1 as any).displayName === (type2 as any).displayName;
  }

  return false;
};

const find = <T>(elements: T[], func: (a: T) => boolean) => elements.find(func);

export const isInputField = (event: KeyboardEvent) =>
  ['INPUT', 'TEXTAREA'].includes(
    get(event, 'target.tagName', '').toUpperCase()
  );

export function mergeAndSortChildren(
  defaultChildren: React.ReactNode[],
  _children: any,
  _parentProps: any,
  defaultOrder = 1
) {
  const children = Children.toArray(_children);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const parentProps = { ..._parentProps };
  delete parentProps.className;
  return children
    .filter((e) => !(e as any).props.disabled) // filter the disabled components
    .concat(
      defaultChildren.filter(
        (c) => !find(children, (component) => isTypeEqual(component, c))
      )
    )
    .map((element: any) => {
      // TODO: narrow the types
      const defaultComponent = find(defaultChildren, (c) =>
        isTypeEqual(c, element)
      );
      delete parentProps.order;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const defaultProps = (defaultComponent as any)?.props || {};
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const props = {
        ...parentProps, // inherit from parent component
        ...defaultProps, // inherit from default component
        ...element.props, // element's own props
      };
      const e = cloneElement(element, props, element.props.children);
      return e;
    })
    .sort(
      (a, b) =>
        (a.props.order || defaultOrder) - (b.props.order || defaultOrder)
    );
}

/**
 * Little function to make logging within Array's fluent API.
 * Logs the value and passes it through.
 * `[1,2,3,4]...map(logit('Count =>'))...`
 * @param {string} [message='LogIt ==>']
 */
export const logit =
  <T>(message = 'LogIt ==>') =>
  (value: T) => {
    console.log(message, value);
    return value;
  };

export const getContrastTextFromBackground = (hexCode: string) => {
  let hexColor = hexCode;
  // If a leading # is provided, remove it
  if (hexColor.slice(0, 1) === '#') {
    hexColor = hexColor.slice(1);
  }

  // If a three-character hexcode, make six-character
  if (hexColor.length === 3) {
    hexColor = hexColor
      .split('')
      .map(function (hex) {
        return hex + hex;
      })
      .join('');
  }

  // Convert to RGB value
  const r = parseInt(hexColor.substring(0, 2), 16);
  const g = parseInt(hexColor.substring(2, 4), 16);
  const b = parseInt(hexColor.substring(4, 6), 16);

  // Get YIQ ratio
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;

  // Check contrast
  return yiq >= 128 ? 'black' : 'white';
};

/** Parse a #rgb or #rrggbb hex string to its r/g/b components (0-255). */
export const hexToRgb = (
  hexCode: string
): { r: number; g: number; b: number } => {
  if (typeof hexCode !== 'string' || !hexCode) {
    return { r: 0, g: 0, b: 0 };
  }
  let hex = hexCode.startsWith('#') ? hexCode.slice(1) : hexCode;
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((c) => c + c)
      .join('');
  }
  return {
    r: parseInt(hex.substring(0, 2), 16) || 0,
    g: parseInt(hex.substring(2, 4), 16) || 0,
    b: parseInt(hex.substring(4, 6), 16) || 0,
  };
};

/** Build an rgba() string from a hex colour and an alpha (0-1). */
export const hexToRgba = (hexCode: string, alpha: number): string => {
  const { r, g, b } = hexToRgb(hexCode);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const capitalize = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1);

export const validateTrue = (val: any) =>
  // only allow more restricted values to evaluate as true
  [true, 'true'].includes(val);
